import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Linking } from 'react-native';

import { Sentry } from '@/util/sentry';

/**
 * Finds URLs in chat message text, including URLs whose path contains
 * parentheses, such as Wikipedia and PubMed query links.
 *
 * GiftedChat parses message text with react-native-parsed-text, whose default
 * URL pattern leaves `(` and `)` out of the path, so a link stops at the first
 * paren and the rest renders as plain text. This keeps that pattern's host rule
 * and lets the path hold one level of matched parentheses, the approach
 * GitHub's autolinker and linkify-it take. An unmatched `)` or a trailing
 * period is never part of the URL, so a link wrapped in prose like
 * "(see https://example.com)" still stops before the closing paren.
 *
 * Do not widen the path to any non-space run: that swallows the closing paren
 * and trailing punctuation around the link, which is why the newer GiftedChat
 * parser is no fix for this.
 */
export const MESSAGE_URL_PATTERN =
  /(https?:\/\/|www\.)[-a-zA-Z0-9@:%._+~#=]{1,256}\.(xn--)?[a-z0-9-]{2,20}\b(?:(?:[-a-zA-Z0-9@:%_+[\],.~#?&/=]|\([^\s()]*\))*(?:[-a-zA-Z0-9@:%_+\]~#?&/=]|\([^\s()]*\)))?/i;

/**
 * Returns a URL a browser can open. A `www.` match has no scheme, and opening
 * it as-is fails.
 */
export function toOpenableUrl(url: string): string {
  return /^www\./i.test(url) ? `https://${url}` : url;
}

/**
 * Opens a link a reader tapped in a chat message.
 *
 * On web it must open a new tab. expo-linking's web `openURL` replaces the
 * current page instead, which takes the reader out of the conversation and
 * drops an unsent draft, so do not swap this for it. Native uses React
 * Native's `Linking` rather than an in-app browser so `mailto:` and `tel:`
 * links still reach their apps.
 */
export function openMessageLink(url: string): void {
  const openableUrl = toOpenableUrl(url);

  if (isWeb) {
    window.open(openableUrl, '_blank', 'noopener,noreferrer');

    return;
  }

  Linking.openURL(openableUrl).catch((error: Error) => {
    Sentry.captureException(error, {
      tags: { component: 'openMessageLink', source: 'chat-message-link' },
      extra: { errorMessage: error?.message },
    });
  });
}

export type MessageLinkKind = 'url' | 'phone' | 'email';

export interface MessageLinkMatch {
  start: number;
  end: number;
  text: string;
  kind: MessageLinkKind;
}

/**
 * The phone and email patterns GiftedChat's text parser uses
 * (react-native-parsed-text), kept identical so a message links the same
 * numbers and addresses whichever renderer draws it.
 */
const MESSAGE_PHONE_PATTERN = /[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,7}/;

const MESSAGE_EMAIL_PATTERN = /\S+@\S+\.\S+/;

const LINK_PATTERNS: readonly { kind: MessageLinkKind; pattern: RegExp }[] = [
  { kind: 'url', pattern: MESSAGE_URL_PATTERN },
  { kind: 'phone', pattern: MESSAGE_PHONE_PATTERN },
  { kind: 'email', pattern: MESSAGE_EMAIL_PATTERN },
];

/**
 * The links in a run of message text, in order: URLs, phone numbers and
 * email addresses. Patterns claim text in that order, as GiftedChat's parser
 * applies them, so a number inside a URL stays part of the URL.
 */
export function findMessageLinks(text: string): MessageLinkMatch[] {
  const accepted: MessageLinkMatch[] = [];

  for (const { kind, pattern } of LINK_PATTERNS) {
    for (const match of text.matchAll(new RegExp(pattern.source, `${pattern.flags}g`))) {
      const start = match.index;

      const end = start + match[0].length;

      const overlaps = accepted.some((taken) => start < taken.end && taken.start < end);

      if (!overlaps) {
        accepted.push({ start, end, text: match[0], kind });
      }
    }
  }

  return accepted.sort((first, second) => first.start - second.start);
}

/**
 * The address a tapped link opens: the page for a URL, the dialer for a phone
 * number, the mail app for an address. GiftedChat asks whether to call or text
 * a number; a tap here calls, which is what a number in an answer is for.
 */
export function toMessageLinkHref(link: Pick<MessageLinkMatch, 'text' | 'kind'>): string {
  if (link.kind === 'phone') {
    return `tel:${link.text.replace(/[^\d+]/g, '')}`;
  }

  if (link.kind === 'email') {
    return `mailto:${link.text}`;
  }

  return link.text;
}
